import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import authService from '../services/auth.service';
import tokenStorage from '../../../utils/tokenStorage';
import toast from 'react-hot-toast';

// Async thunks
export const updateProfile = createAsyncThunk(
  'auth/updateProfile',
  async (profileData, { rejectWithValue }) => {
    try {
      // Extract silent flag and remove it from profileData before sending to API
      const { silent, ...dataToSend } = profileData;
      const response = await authService.updateProfile(dataToSend);
      // Only show success toast if silent flag is not set
      if (!silent) {
        toast.success('Profile updated successfully');
      }
      return response;
    } catch (error) {
      const message = error.response?.data?.message || error.message || 'Failed to update profile';
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);

/**
 * Auth Slice
 * Manages authentication state
 */
const initialState = {
  user: null,
  accessToken: null,
  refreshToken: null,
  isAuthenticated: false,
  mustChangePassword: false,
  loading: false,
  error: null,
  initialized: false,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    initializeAuth: (state) => {
      state.initialized = true;
      // Check if we have a valid access token in memory
      const accessToken = tokenStorage.getAccessToken();
      const userData = localStorage.getItem('userData');
      
      if (accessToken && userData) {
        try {
          // Token is already validated by tokenStorage
          state.accessToken = accessToken;
          state.user = JSON.parse(userData);
          state.isAuthenticated = true;
        } catch (error) {
          // Invalid user data, clear auth state
          tokenStorage.clearAccessToken();
          localStorage.removeItem('userData');
          state.user = null;
          state.accessToken = null;
          state.isAuthenticated = false;
        }
      } else {
        // No valid token, clear auth state
        tokenStorage.clearAccessToken();
        localStorage.removeItem('userData');
        state.user = null;
        state.accessToken = null;
        state.isAuthenticated = false;
      }
    },
    loginStart: (state) => {
      state.loading = true;
      state.error = null;
    },
    loginSuccess: (state, action) => {
      state.loading = false;
      state.initialized = true;
      state.isAuthenticated = true;
      state.user = action.payload.user;
      state.accessToken = action.payload.accessToken;
      state.mustChangePassword = action.payload.mustChangePassword || false;
      // Store access token securely in memory only
      tokenStorage.setAccessToken(action.payload.accessToken);
      // Store only user data in localStorage (no sensitive tokens)
      localStorage.setItem('userData', JSON.stringify(action.payload.user));
    },
    loginFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    logout: (state) => {
      state.user = null;
      state.accessToken = null;
      state.refreshToken = null;
      state.isAuthenticated = false;
      state.mustChangePassword = false;
      // Clear secure token storage
      tokenStorage.clearAccessToken();
      // Clear user data from localStorage
      localStorage.removeItem('userData');
      // Clear selected address from localStorage
      localStorage.removeItem('selectedAddress');
    },
    updateUser: (state, action) => {
      // Replace entire user object to ensure nested updates (like profile.addresses) work correctly
      state.user = action.payload;
      localStorage.setItem('userData', JSON.stringify(state.user));
    },
    clearPasswordChangeRequirement: (state) => {
      state.mustChangePassword = false;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Update profile
      .addCase(updateProfile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateProfile.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload.user) {
          state.user = action.payload.user;
        } else {
          state.user = { ...state.user, ...action.payload.data };
        }
        localStorage.setItem('userData', JSON.stringify(state.user));
      })
      .addCase(updateProfile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const {
  initializeAuth,
  loginStart,
  loginSuccess,
  loginFailure,
  logout,
  updateUser,
  clearPasswordChangeRequirement,
  clearError,
} = authSlice.actions;

export default authSlice.reducer;


